import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	clearFerryBookingTransactionIdFromStorage,
	SplitBooking,
} from 'Services/Api/_HumanWritten/BookingService/BookingService';
import {
	clearBookingPricingCache,
	useBookingWizardPricingCacheStore,
} from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardPricingCacheStore';
import { LottieWaveDotLoader } from 'Views/Components/_HumanWritten/Lottie/LottieWaveDotLoader';
import TripBookingSummaryCard, { tripSummaryLocationType } from './TripBookingSummaryCard';
import { BookingEntity } from 'Models/Entities';
import TripBookingSummaryTotal from './TripBookingSummaryTotal';
import classNames from 'classnames';
import { feeType } from 'Models/Enums';
import { isNotNullOrUndefined, isNullOrUndefined } from 'Util/TypeGuards';
import { SelectedTrips } from '../../BookingWizardWrap';
import alertToast from 'Util/ToastifyUtils';
import {
	trackAddToCart,
	trackRemoveFromCart,
	trackStartEditBooking,
} from 'Util/_HumanWritten/Analytics/analyticsEvents';
import {
	bookingItemFields,
	bookingsToItems,
	bookingsTotalValue,
	flattenBookingLegs,
} from 'Util/_HumanWritten/Analytics/analyticsBookingFields';
import useFetchBookingPriceBreakdown from 'Hooks/useFetchBookingPrice';
import { ferryBookingToWizardData } from 'Util/_HumanWritten/BookingWizard/FerryBookingToWizardDataUtils';
import { removeBookingFromCart } from 'Services/Api/_HumanWritten/BookingService/FerryTripBookingService';
import { BookingWizardData, passengerSeatingSignature } from '../../BookingWizardData/BaseBookingWizardData';
import { getWizardDataVehicleWizardDataDependencies } from '../../BookingWizardData/VehicleBookingWizardData';
import { clearOldWizardData, getDefaultWizardData, useBookingWizardDataStore } from '../../BookingWizardDataStore';
import { PassengerBookingWizardData } from '../../BookingWizardData/PassengerBookingWizardData';
import { FreightBookingWizardData } from '../../BookingWizardData/FreightBookingWizardData';
import { action } from 'mobx';
import { cloneDeep } from 'lodash';
import useDeviceDetect from 'Hooks/useDeviceDetect';
import { editReturnBookingModal } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Cart/EditReturnBookingModal';
import { BookingWizardCartFields } from '../../BookingWizardTypes';
import { calculateBookingsToDisplay } from './TripBookingSummaryHelpers';
import LoadingWave from 'Views/Components/_HumanWritten/Lottie/LoadingWave';
import { ParkingReservationSummaryCard } from './ParkingReservationSummaryCard';
import { ParkingReservationSummaryDto } from 'Services/Api/_HumanWritten/ParkingService';
import { appConfigStore } from 'Models/AppConfigStore';
import { useParkingWizardStore } from 'Stores/ParkingWizardStore';
import { MarketingSubscriptionCheckbox } from 'Views/Components/_HumanWritten/Marketing/MarketingSubscriptionCheckbox';
import { funnelEventAlreadyTracked, markFunnelEventTracked } from 'Util/_HumanWritten/Analytics/funnelDedupe';
import ClassBookingSummaryCard from './ClassBookingSummary/ClassBookingSummaryCard';
import ClassBookingSummaryTotal from './ClassBookingSummary/ClassBookingSummaryTotal';

export interface TripBookingSummaryCardsInnerProps {
	bookings: BookingEntity[];
	wizardData?: BookingWizardData;
	onClearGiftCertificate?: (id: string) => void;
	onClearFee?: (feeId: string) => void;
	onRemoveManagerDiscount?: () => void;
	onRemoveDiscount?: (discountId: string) => void;
	onRemoveFee?: (feeType: feeType, departureDiscountAmount?: number, returnDiscountAmount?: number) => void;
	/**
	 * The location that the trip summary is being rendered
	 */
	tripSummaryLocation: tripSummaryLocationType;
	/**
	 * Looks like Check-In is the only component that uses this prop...
	 */
	alterReturn?: boolean;
	selectedTrips?: SelectedTrips;
	setSelectedTrips?: (trips: SelectedTrips) => void;
	onUpdateWizardData?: (data: BookingWizardData) => void;
	refresh?: () => Promise<BookingEntity[]>;
	refetch?: boolean;
	setRefetch?: (refetch: boolean) => void;
	/**
	 * Refreshes the totals and resolves once they are showing — handed to the fare-class summary
	 * total, which passes it to the voucher field. See onVoucherApplied there.
	 *
	 * <p>Only the fare-class summary takes it. The older summary below keeps toggling `refetch`, so
	 * nothing about its behaviour changes.</p>
	 */
	onVoucherApplied?: () => Promise<unknown>;
	transactionId?: string;
	includeCreditCardFee?: boolean;
	setPriceInParentComponent?: (price?: number) => void;
	showQrCodes?: boolean;
	hideLinks?: boolean;
	parkingReservations?: ParkingReservationSummaryDto[];
	/**
	 * Render only the booking cards (no surrounding row container and no total card). Used by the
	 * booking-success page to lay several split pay-later transactions out as a single summary with
	 * one combined total card, while each card still gets its own transaction's pricing.
	 */
	cardsOnly?: boolean;
}

function TripBookingSummaryInner({
	bookings,
	wizardData,
	onRemoveManagerDiscount,
	onRemoveFee,
	tripSummaryLocation,
	alterReturn,
	selectedTrips,
	setSelectedTrips,
	onClearGiftCertificate,
	onRemoveDiscount,
	refresh,
	refetch,
	setRefetch,
	onVoucherApplied,
	onUpdateWizardData,
	transactionId,
	onClearFee,
	includeCreditCardFee = true,
	setPriceInParentComponent,
	showQrCodes,
	hideLinks,
	parkingReservations,
	cardsOnly,
}: TripBookingSummaryCardsInnerProps) {
	const navigate = useNavigate();

	const afterPayment = tripSummaryLocation === 'booking-success';
	const cartView = tripSummaryLocation === 'cart' || tripSummaryLocation === 'check-in';

	const { isIpad } = useDeviceDetect();
	const { setOldWizardData } = useBookingWizardDataStore();

	const wizardDataDependencies = useMemo(
		() =>
			getWizardDataVehicleWizardDataDependencies(
				[
					'cargoTypeId',
					'vehicleLengthId',
					'vehicleWeightId',
					'vehicleWidthId',
					'showWidthDetails',
					'trailerTypeId',
					'trailerLengthId',
					// The trailer weight band changes when the customer edits the trailer's exact weight,
					// so it must trigger a price re-fetch (parallel to vehicleWeightId).
					'trailerWeightId',
					// Exact weight must trigger a re-fetch directly: when the entered weight stays within the
					// same band (e.g. the minimum band, costing $0), vehicleWeightId/trailerWeightId don't
					// change — so without these the sidebar price wouldn't recompute and the weight line
					// (now shown even at $0) would never appear.
					'vehicleExactWeight',
					'vehicleExactWeightUnit',
					'trailerExactWeight',
					'trailerExactWeightUnit',
					// The trailer has its own width (parallel to vehicleWidthId/showWidthDetails), so
					// changing it must re-fetch the price — otherwise the width line wouldn't update.
					'trailerWidthId',
					'showTrailerWidthDetails',
				],
				wizardData,
			),
		[wizardData],
	);

	const { data: transactionInfo, refetch: refetchTransactionInfo } = useFetchBookingPriceBreakdown(
		tripSummaryLocation,
		[
			...wizardDataDependencies,
			// A class carries its own fare and its own seat-selection fee, and both live inside the
			// eight passenger arrays rather than as a field of their own — so nothing else in this
			// list moves when a booker changes class or seat, and the sidebar kept the old price.
			passengerSeatingSignature(wizardData),
			// Switching the selected customer changes their user pricing, so the sidebar must re-price.
			// Without userId here the effect won't re-fire when only the customer changes (e.g. passenger
			// bookings, where nothing else in this dep array moves) and the previous user's price sticks.
			wizardData?.userId,
			// We need to stringify the add-ons or they will not trigger a re-render
			JSON.stringify((wizardData?.innerWizardData as PassengerBookingWizardData)?.departingTripOptions),
			JSON.stringify((wizardData?.innerWizardData as PassengerBookingWizardData)?.returningTripOptions),
			(wizardData?.innerWizardData as FreightBookingWizardData)?.handlingIds,
			(wizardData?.innerWizardData as FreightBookingWizardData)?.freightTypeId,
			(wizardData?.innerWizardData as FreightBookingWizardData)?.freightCategoryId,
			(wizardData?.innerWizardData as FreightBookingWizardData)?.quantity,
			(wizardData?.innerWizardData as FreightBookingWizardData)?.weight,
			(wizardData?.innerWizardData as FreightBookingWizardData)?.height,
			(wizardData?.innerWizardData as FreightBookingWizardData)?.length,
			(wizardData?.innerWizardData as FreightBookingWizardData)?.width,
			refetch,
			bookings,
		],
		transactionId,
		wizardData,
		includeCreditCardFee,
		false,
		setPriceInParentComponent,
	);

	const isAlteration = (wizardData?.wizardMode === 'ALTERATION' || transactionInfo?.isAlteration) ?? false;

	const { setBookingPrice, removeBookingPrice } = useBookingWizardPricingCacheStore();

	// Build parking cards: use server data (prop) for booking success, Zustand store for sidebar/cart
	const parkingStore = useParkingWizardStore();
	const parkingCards = useMemo<ParkingReservationSummaryDto[]>(() => {
		if (!appConfigStore.parkingEnabled) return [];

		// After payment, use server-fetched reservations passed as prop
		if (afterPayment && parkingReservations && parkingReservations.length > 0) {
			return parkingReservations;
		}

		// During wizard (sidebar/cart), build mock reservations from Zustand store
		if (!parkingStore.quote || parkingStore.quote.length === 0) return [];

		// Group vehicles by locationId
		const byLocation = new Map<
			string,
			{
				quote: (typeof parkingStore.quote)[number];
				detail: (typeof parkingStore.vehicleDetails)[number];
			}[]
		>();

		parkingStore.quote.forEach((q, i) => {
			const detail = parkingStore.vehicleDetails[i];
			if (!detail) return;
			const locId = detail.locationId ?? parkingStore.locationId ?? 'unknown';
			if (!byLocation.has(locId)) {
				byLocation.set(locId, []);
			}
			byLocation.get(locId)!.push({ quote: q, detail });
		});

		return Array.from(byLocation.entries()).map(([locId, items]) => ({
			id: `mock-${locId}`,
			humanReadableId: '',
			status: 'PENDING',
			totalPrice: items.reduce((sum, item) => sum + (item.quote.subTotal ?? 0), 0),
			parkingLocationId: locId,
			locationName: parkingStore.locationNames[locId] ?? 'Parking',
			vehicles: items.map((item, vi) => ({
				id: `mock-v-${locId}-${vi}`,
				parkingVehicleTypeId: item.detail.parkingVehicleTypeId ?? '',
				parkingVehicleTypeName: parkingStore.vehicleTypeNames[item.detail.parkingVehicleTypeId ?? ''] ?? '',
				parkingVehicleTypeIconName: undefined,
				registration: item.detail.registration ?? '',
				startDate: item.detail.startDate,
				endDate: item.detail.endDate,
				dailyRate: item.quote.dailyRate ?? 0,
				subTotal: item.quote.subTotal ?? 0,
			})),
		}));
	}, [
		afterPayment,
		parkingReservations,
		parkingStore.quote,
		parkingStore.vehicleDetails,
		parkingStore.locationId,
		parkingStore.locationNames,
		parkingStore.vehicleTypeNames,
	]);

	// Helper function to calculate bookings to display (extracted for reuse)

	// If it is cartView only show the bookings that are stored in the local storage
	// Else if it is not cartView (sideBarSummary) then show the already existing bookings and append the current
	// in progress booking to the list of bookings to render in the side bar
	const bookingsToDisplay = useMemo(
		() =>
			calculateBookingsToDisplay(
				bookings,
				cartView,
				afterPayment,
				isAlteration,
				tripSummaryLocation,
				wizardData,
				selectedTrips,
				alterReturn,
			),
		[bookings, wizardData, selectedTrips, isAlteration, cartView, afterPayment, tripSummaryLocation, alterReturn],
	);

	// Fire `add_to_cart` once when the customer reaches the booking-wizard cart, built from the
	// actual reserved bookings (each item typed from its own summary, both return legs included)
	// — not the pricing cache, which could include removed/stale entries.
	//
	// The ref covers a repeat within this mount; `funnelDedupe` covers returning to the cart from
	// a later step, which remounts this component and resets the ref. That matters more here than
	// elsewhere in the funnel because the event reports the WHOLE cart rather than a delta, so a
	// repeat double-counts every item's value and quantity.
	const addToCartTrackedRef = React.useRef(false);
	useEffect(() => {
		if (tripSummaryLocation !== 'cart' || addToCartTrackedRef.current || bookings.length === 0) {
			return;
		}
		if (funnelEventAlreadyTracked('add_to_cart')) {
			return;
		}
		addToCartTrackedRef.current = true;
		markFunnelEventTracked('add_to_cart');
		// Cart prices live in the pricing cache (summary totalCost isn't set yet in the wizard).
		const { getBookingPrice } = useBookingWizardPricingCacheStore.getState();
		const priceOf = (b: BookingEntity) => getBookingPrice(b.id);
		const legs = flattenBookingLegs(bookings);
		trackAddToCart({
			value: bookingsTotalValue(legs, priceOf),
			items: bookingsToItems(legs, priceOf),
		});
	}, [tripSummaryLocation, bookings]);

	const onRemoveBooking = async (
		bookingId: string,
		removeAssociatedBooking: boolean,
		departureBooking: boolean,
	): Promise<boolean> => {
		// Update wizard data if necessary for bulk bookings
		if (shouldUpdateWizardData(bookingId)) {
			const newData = updateWizardDataForRemoval(bookingId);
			if (onUpdateWizardData) {
				onUpdateWizardData(newData);
			}
		}
		clearOldWizardData();

		// Capture the price + booking type before removal so the event can report them.
		const removedPrice = useBookingWizardPricingCacheStore.getState().getBookingPrice(bookingId);
		const removedSummary = bookings.find(b => b.id === bookingId)?.bookingSummaries?.recentSummary;
		const removedItemType = bookingItemFields({
			hasCargo: isNotNullOrUndefined(removedSummary?.cargoInfo),
			hasFreight: isNotNullOrUndefined(removedSummary?.freightDetail),
		});

		removeBookingPrice(bookingId);

		// Remove a reserved booking
		const result = await removeBookingFromCart(bookingId, removeAssociatedBooking, departureBooking);

		if (result.status !== 200) {
			alertToast('There was an error removing this booking.', 'error', 'Failed to remove booking');
			return false;
		}

		alertToast('The booking has been removed from your cart.', 'success', 'Booking removed');

		trackRemoveFromCart({
			value: removedPrice,
			items: [
				{
					...removedItemType,
					price: removedPrice,
					quantity: 1,
				},
			],
		});

		// Refresh to get the updated bookings list
		let updatedBookings: BookingEntity[] = bookings;
		if (refresh) {
			updatedBookings = await refresh();
		}

		// If that was the last booking in the cart, clear the stale booking + transaction references.
		// bookingToEdit and the old wizard data otherwise persist, so the next reservation would alter
		// the now-expired booking (resurrecting the old transaction and leaving it with >1 alteration,
		// which breaks the "only new bookings" check) instead of creating a fresh booking. Safe to
		// update state here — this is an event handler, not render.
		if (updatedBookings.length === 0) {
			clearFerryBookingTransactionIdFromStorage();
			clearOldWizardData();
			if (onUpdateWizardData) {
				onUpdateWizardData(getDefaultWizardData({ userId: wizardData?.userId }));
			}
		}

		// Update wizard data with one of the remaining bookings
		if (onUpdateWizardData && !shouldUpdateWizardData(bookingId)) {
			if (updatedBookings.length > 0) {
				// Find a booking that isn't the one we just removed
				const remainingBooking = updatedBookings.find(b => b.id !== bookingId);

				if (remainingBooking) {
					// Convert the remaining booking to wizard data
					const reconstructedCartFields = ferryBookingToWizardData(
						[remainingBooking],
						undefined,
						undefined,
						isAlteration,
						// we want this to be undefined so that the user can add a return booking back onto it
						true,
					);

					if (reconstructedCartFields?.[0]) {
						const newData = new BookingWizardData({ ...reconstructedCartFields[0].wizardData });
						onUpdateWizardData(newData);
					} else {
						// Fallback to default if conversion fails
						onUpdateWizardData(getDefaultWizardData({ userId: wizardData?.userId }));
					}
				} else {
					// No remaining bookings, use default
					onUpdateWizardData(getDefaultWizardData({ userId: wizardData?.userId }));
				}
			} else {
				// No bookings left, use default
				onUpdateWizardData(getDefaultWizardData({ userId: wizardData?.userId }));
			}
		}

		return true;
	};

	const shouldUpdateWizardData = (bookingId: string) => {
		return wizardData && bookingId;
	};

	const updateWizardDataForRemoval = (bookingId: string) => {
		const booking = bookings.find(b => b.id === bookingId);
		const newData = new BookingWizardData({ ...wizardData });
		return newData as BookingWizardData;
	};

	const onEdit = action(async (newBookingToEdit: BookingWizardCartFields) => {
		if (setSelectedTrips) {
			// setSelectedTrips(newBookingToEdit.selectedTrips);
		}

		if (onUpdateWizardData) {
			const newData = new BookingWizardData({ ...newBookingToEdit.wizardData });
			onUpdateWizardData(newData);
			// We need to clone the data because the old wizard data is stored in a state variable and
			// we don't want to update the state variable when the regular wizard data is updated
			setOldWizardData(new BookingWizardData(cloneDeep(newBookingToEdit.wizardData)));
		}
	});

	const handleEditBooking = async (
		originalCartFields: BookingWizardCartFields,
		departureBooking: boolean,
		originalBookingEntity: BookingEntity,
		/**
		 * Which trips to edit, when the caller has already asked (the fare-type summary's single-step
		 * modal). Left out, a return booking is asked here as it always was.
		 */
		bothTrips?: boolean,
	) => {
		trackStartEditBooking({ booking_id: originalBookingEntity.id });

		const handleAlterReturnTrip = async (value: boolean) => {
			let cartFieldsToEdit: BookingWizardCartFields | undefined;

			if (value === false) {
				// Split the booking and get the ID of the booking we want to edit
				const bookingIdToEdit = await SplitBooking(originalBookingEntity.id, departureBooking);

				let updatedBookings: BookingEntity[] = bookings;

				if (refresh) {
					// Get the fresh bookings directly from refresh
					updatedBookings = await refresh();
				}

				// Find the split booking in the updated bookings list
				const splitBookingEntity = updatedBookings.find(b => b.id === bookingIdToEdit);

				if (splitBookingEntity) {
					// Remove the old booking price from cache since it's been split
					removeBookingPrice(originalBookingEntity.id);

					// After split, we need to get the pricing for both new bookings
					// The split creates two separate one-way bookings from one return booking
					// We need to trigger a refetch of pricing so the cache gets updated
					// Fetch the updated pricing and wait for it to complete
					const updatedTransactionInfo = await refetchTransactionInfo();

					// Now set the booking prices for the new split bookings
					if (updatedTransactionInfo) {
						for (const x of updatedTransactionInfo.linkedBookings) {
							if (x.departureBooking.bookingId) {
								let total = x.departureBooking.totalPrice;
								if (x.returnBooking?.bookingId) {
									total += x.returnBooking.totalPrice;
								}
								setBookingPrice(x.departureBooking.bookingId, total);
							}
						}
					}

					// Use ferryBookingToWizardData to reconstruct cart fields from the fresh booking
					const reconstructedCartFields = ferryBookingToWizardData(
						[splitBookingEntity],
						undefined,
						undefined,
						isAlteration,
						false, // It's now one-way after split
					);

					cartFieldsToEdit = reconstructedCartFields?.[0];
				}
			} else {
				// No split, use the original cart fields
				cartFieldsToEdit = originalCartFields;
			}

			// Only navigate if we have valid cart fields
			if (cartFieldsToEdit) {
				await onEdit(cartFieldsToEdit);
				navigate(isIpad ? '/booking-wizard/search' : '/booking-wizard/tickets');
			} else {
				alertToast(
					'Unable to load booking data. Please refresh the page and try again.',
					'error',
					'Error loading booking',
				);
			}
		};

		if (bothTrips !== undefined) {
			await handleAlterReturnTrip(bothTrips);
		} else if (originalCartFields.returningJourney) {
			await editReturnBookingModal(handleAlterReturnTrip);
		} else {
			// We pass true in so that it doesn't do anything on the server,
			// even though we are only editing a one-way trip
			await handleAlterReturnTrip(true);
		}
	};

	useEffect(() => {
		if (wizardData?.isBulkBookingFlow) {
			return;
		}
		if (isNotNullOrUndefined(transactionInfo)) {
			// We want to store the price of each booking so that we don't have to recalculate when the user is
			// adding another booking (note: we show the old booking in the sidebar with the total price).
			for (const x of transactionInfo?.linkedBookings) {
				if (x.departureBooking.bookingId) {
					let total = x.departureBooking.totalPrice;
					if (x.returnBooking?.bookingId) {
						total += x.returnBooking.totalPrice;
					}
					setBookingPrice(x.departureBooking.bookingId, total);
				}
			}
		}
	}, [transactionInfo, bookings]);

	const listOfBookings = bookingsToDisplay?.map((booking, count) => {
		const index = count;

		if (isNullOrUndefined(booking)) {
			return null;
		}
		return (
			<TripBookingSummaryCard
				key={index}
				count={index}
				booking={bookings.find(x => x.id === booking.wizardData.bookingToEdit) ?? bookings[0]}
				cartFields={booking}
				wizardData={wizardData}
				bookingInfo={
					tripSummaryLocation === 'sidebar'
						? transactionInfo?.linkedBookings[0]
						: transactionInfo?.linkedBookings.find(
								x => x.departureBooking.bookingId === booking.wizardData.bookingToEdit,
							)
				}
				isAlteration={isAlteration ?? false}
				tripSummaryLocation={tripSummaryLocation}
				selectedTrips={selectedTrips}
				setSelectedTrips={setSelectedTrips}
				afterPayment={afterPayment}
				cartView={cartView}
				onRemoveBooking={onRemoveBooking}
				onEditBooking={handleEditBooking}
				showQrCodes={showQrCodes}
				hideLinks={hideLinks}
			/>
		);
	});

	if (isNullOrUndefined(transactionInfo) && (cartView || wizardData?.tabSelected !== 'freight')) {
		return <LoadingWave>Loading cart</LoadingWave>;
	}

	if (isNullOrUndefined(listOfBookings)) {
		return <LottieWaveDotLoader />;
	}

	if (listOfBookings.length === 0) {
		clearFerryBookingTransactionIdFromStorage();
		clearBookingPricingCache();
		navigate('/booking-wizard/tickets');
		return <></>;
	}

	// Sites with fare types get the grouped summary in the cart, the sidebar and check-in's review page.
	// Freight has no fare types, and the success page has its own layout, so each keeps the current one.
	// TEMPORARY: every other site is back on the old summary for now; designs 13a, 13d and 13e move them
	// across by dropping the `passengerClassesEnabled` check below.
	const classSummaryLocation =
		tripSummaryLocation === 'cart' || tripSummaryLocation === 'sidebar' || tripSummaryLocation === 'check-in'
			? tripSummaryLocation
			: null;
	if (
		appConfigStore.passengerClassesEnabled &&
		classSummaryLocation !== null &&
		!cardsOnly &&
		bookingsToDisplay?.every(x => x?.wizardData.tabSelected !== 'freight')
	) {
		return (
			<div
				className={classNames('class-booking-summary', `class-booking-summary--${classSummaryLocation}`)}
				data-test-id={`class-booking-summary-${classSummaryLocation}`}
			>
				<div className="class-booking-summary__bookings">
					{bookingsToDisplay?.map((booking, index) =>
						booking ? (
							<ClassBookingSummaryCard
								// eslint-disable-next-line react/no-array-index-key
								key={index}
								count={index}
								booking={bookings.find(x => x.id === booking.wizardData.bookingToEdit) ?? bookings[0]}
								cartFields={booking}
								wizardData={wizardData}
								bookingInfo={
									classSummaryLocation === 'sidebar'
										? transactionInfo?.linkedBookings[0]
										: transactionInfo?.linkedBookings.find(
												x => x.departureBooking.bookingId === booking.wizardData.bookingToEdit,
											)
								}
								location={classSummaryLocation}
								isAlteration={isAlteration ?? false}
								onRemoveBooking={onRemoveBooking}
								onEditBooking={handleEditBooking}
							/>
						) : null,
					)}
					{tripSummaryLocation === 'cart' && wizardData && onUpdateWizardData && (
						<MarketingSubscriptionCheckbox
							userId={wizardData.userId}
							value={wizardData.marketingSubscriptionAcknowledgment}
							onChange={value => {
								const next = new BookingWizardData({ ...wizardData });
								next.marketingSubscriptionAcknowledgment = value;
								onUpdateWizardData(next);
							}}
						/>
					)}
				</div>
				<ClassBookingSummaryTotal
					transactionPricingResults={transactionInfo ?? undefined}
					location={classSummaryLocation}
					wizardData={wizardData}
					onClearGiftCertificate={onClearGiftCertificate}
					onRemoveDiscount={onRemoveDiscount}
					onRemoveManagerDiscount={onRemoveManagerDiscount}
					onRemoveFee={onRemoveFee}
					onClearFee={onClearFee}
					refetch={refetch}
					setRefetch={setRefetch}
					onVoucherApplied={onVoucherApplied}
					bookingIds={bookings.map(x => x.id)}
				/>
			</div>
		);
	}

	const isCartView = cartView || afterPayment;
	const clazz = classNames('trip-booking-summary-card__container', {
		'cart-view': isCartView,
		'sidebar-view': !isCartView,
	});

	// Cards-only: emit just the booking cards (no row container, no total card) so a caller can
	// compose several summaries into one layout with a single shared total card. Each card still
	// uses this instance's own transaction pricing, so per-booking prices stay correct.
	if (cardsOnly) {
		return <>{listOfBookings}</>;
	}

	return (
		<div className={clazz}>
			<div className="bookings-list__scrollable">
				{listOfBookings}
				{/* TODO: Re-enable parking summary cards when booking wizard integration is complete
				{parkingCards.map(reservation => (
					<ParkingReservationSummaryCard
						key={reservation.id}
						reservation={reservation}
						hideLinks={hideLinks || !afterPayment}
						showReservationId={afterPayment}
					/>
				))}
				*/}
				{tripSummaryLocation === 'cart' && wizardData && onUpdateWizardData && (
					<MarketingSubscriptionCheckbox
						userId={wizardData.userId}
						value={wizardData.marketingSubscriptionAcknowledgment}
						onChange={value => {
							const next = new BookingWizardData({ ...wizardData });
							next.marketingSubscriptionAcknowledgment = value;
							onUpdateWizardData(next);
						}}
					/>
				)}
			</div>
			<TripBookingSummaryTotal
				transactionPricingResults={transactionInfo ?? undefined}
				afterPayment={afterPayment}
				cartView={cartView}
				wizardData={wizardData}
				onClearGiftCertificate={onClearGiftCertificate}
				onRemoveDiscount={onRemoveDiscount}
				onRemoveManagerDiscount={onRemoveManagerDiscount}
				onRemoveFee={onRemoveFee}
				onClearFee={onClearFee}
				tripSummaryLocation={tripSummaryLocation}
				refetch={refetch}
				setRefetch={setRefetch}
				bookingIds={bookings.map(x => x.id)}
			/>
		</div>
	);
}

export default TripBookingSummaryInner;
